import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { API_URL } from "@/config/api";

// Context to control WhatsApp bubble visibility
export const WhatsAppBubbleContext = createContext({
  hideWhatsAppBubble: false,
  setHideWhatsAppBubble: () => {}
});

export const useWhatsAppBubble = () => useContext(WhatsAppBubbleContext);

const DEFAULT_WHATSAPP_NUMBER = "+33278780404";
const WHATSAPP_MESSAGE = encodeURIComponent("Bonjour, j'ai une question concernant Y GO Seamen Shore Services.");

export default function WhatsAppBubble({ forceHide = false }) {
  const [showLabel, setShowLabel] = useState(true);
  const [adminWhatsApp, setAdminWhatsApp] = useState(DEFAULT_WHATSAPP_NUMBER);
  const { hideWhatsAppBubble } = useWhatsAppBubble();
  
  // Fetch admin WhatsApp number from config
  useEffect(() => {
    const fetchAdminWhatsApp = async () => {
      try {
        // Use a public endpoint to get the support number
        const response = await axios.get(`${API_URL}/api/config/support-contact`);
        if (response.data?.admin_whatsapp) {
          setAdminWhatsApp(response.data.admin_whatsapp);
        }
      } catch (error) {
        // Silently fall back to default
        console.log("Using default support number");
      }
    };
    
    fetchAdminWhatsApp();
  }, []);
  
  const whatsappUrl = `https://wa.me/${adminWhatsApp.replace(/[\s+]/g, '')}?text=${WHATSAPP_MESSAGE}`;

  // Toggle label visibility periodically
  useEffect(() => {
    const showLabelDuration = 5000;
    const hideLabelDuration = 10000;
    
    const toggleLabel = () => {
      setShowLabel(true);
      setTimeout(() => setShowLabel(false), showLabelDuration);
    };

    // Initial hide after 5 seconds
    setTimeout(() => setShowLabel(false), showLabelDuration);
    
    // Periodic toggle
    const interval = setInterval(toggleLabel, hideLabelDuration + showLabelDuration);
    
    return () => clearInterval(interval);
  }, []);

  // Hide bubble if forceHide or context says so
  if (forceHide || hideWhatsAppBubble) {
    return null;
  }

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 left-6 z-50 group"
      data-testid="whatsapp-bubble"
      aria-label="Contact client support"
    >
      <div className="relative flex items-center">
        {/* Avatar Button with Sarah photo */}
        <div className="relative w-14 h-14 rounded-full overflow-hidden shadow-lg border-3 border-white hover:scale-110 transition-transform duration-300">
          {/* Sarah Avatar for Support */}
          <img 
            src="/support-sarah.png" 
            alt="Client Support - Sarah" 
            className="w-full h-full object-cover"
          />
          
          {/* Pulse animation ring */}
          <span className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-50"></span>
        </div>
        
        {/* Label that appears periodically */}
        <div 
          className={`absolute left-full ml-3 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap transition-all duration-300 ${
            showLabel ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none'
          }`}
        >
          <span className="font-medium">Contact client support</span>
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-slate-900 rotate-45"></div>
        </div>
      </div>
    </a>
  );
}
